import { ofType } from "redux-observable";
import { mergeMap, map, catchError } from "rxjs/operators";
import { forkJoin, of } from "rxjs";
import moment from "moment";
import momentTz from "moment-timezone";
import { dispatch } from "../../common/helpers";
import { CSV_DATE_FORMAT, DEFAULT_TIME } from "../../common/constants";
import {
  deviceService,
  patientDeviceService,
  usersService,
} from "../../services";
import timezones from "../../common/timezones.json";

import {
  FETCH_DEVICE_REQUEST,
  FETCH_DEVICE_SUCCESS,
  FETCH_DEVICE_ERROR,
  FETCH_DEVICE_PROPERTY_REQUEST,
  FETCH_DEVICE_PROPERTY_SUCCESS,
  FETCH_DEVICE_PROPERTY_ERROR,
  GET_DEVICE_DETAIL_REQUEST,
  GET_DEVICE_DETAIL_SUCCESS,
  GET_DEVICE_DETAIL_ERROR,
  GET_DEVICE_DETAIL_PROPERTY_ERROR,
  FETCH_DEVICE_PROPERTY_SET_REQUEST,
  FETCH_DEVICE_PROPERTY_SET_SUCCESS,
  FETCH_DEVICE_PROPERTY_SET_ERROR,
  ADD_DEVICE_REQUEST,
  ADD_DEVICE_SUCCESS,
  ADD_DEVICE_ERROR,
  DELETE_DEVICE_REQUEST,
  DELETE_DEVICE_ERROR,
  UPDATE_DEVICE_REQUEST,
  UPDATE_DEVICE_SUCCESS,
  UPDATE_DEVICE_ERROR,
  CREATE_PROPERTY_SET_REQUEST,
  CREATE_PROPERTY_SET_SUCCESS,
  CREATE_PROPERTY_SET_ERROR,
  DELETE_PROPERTY_SET_REQUEST,
  DELETE_PROPERTY_SET_ERROR,
  RESET_REQUEST,
  RESET_SUCCESS,
  FETCH_PROPERTY_SET_DETAIL_REQUEST,
  FETCH_PROPERTY_SET_DETAIL_SUCCESS,
  FETCH_PROPERTY_SET_DETAIL_ERROR,
  UPDATE_PROPERTY_SET_REQUEST,
  UPDATE_PROPERTY_SET_SUCCESS,
  UPDATE_PROPERTY_SET_ERROR,
  GET_DEVICE_PROPERTY_REQUEST,
  GET_DEVICE_PROPERTY_SUCCESS,
  GET_DEVICE_PROPERTY_ERROR,
  UPDATE_DEVICE_PROPERTY_REQUEST,
  UPDATE_DEVICE_PROPERTY_SUCCESS,
  UPDATE_DEVICE_PROPERTY_ERROR,
  CREATE_DEVICE_PROPERTY_REQUEST,
  CREATE_DEVICE_PROPERTY_SUCCESS,
  CREATE_DEVICE_PROPERTY_ERROR,
  DELETE_PROPERTY_REQUEST,
  DELETE_PROPERTY_ERROR,
  FETCH_DEVICE_FIRMWARE_REQUEST,
  FETCH_DEVICE_FIRMWARE_SUCCESS,
  FETCH_DEVICE_FIRMWARE_ERROR,
  FETCH_FIRMWARE_DETAIL_REQUEST,
  FETCH_FIRMWARE_DETAIL_SUCCESS,
  FETCH_FIRMWARE_DETAIL_ERROR,
  UPDATE_FIRMWARE_DETAIL_REQUEST,
  UPDATE_FIRMWARE_DETAIL_SUCCESS,
  UPDATE_FIRMWARE_DETAIL_ERROR,
  CREATE_FIRMWARE_DETAIL_REQUEST,
  CREATE_FIRMWARE_DETAIL_SUCCESS,
  CREATE_FIRMWARE_DETAIL_ERROR,
  DELETE_DEVICE_FIRMWARE_REQUEST,
  DELETE_DEVICE_FIRMWARE_ERROR,
  DUPLICATE_DEVICE_REQUEST,
  DUPLICATE_DEVICE_SUCCESS,
  DUPLICATE_DEVICE_ERROR,
  RESET_GRAPH_REQUEST,
  RESET_GRAPH_SUCCESS,
  FETCH_DEVICE_SPECIFIED_REQUEST,
  FETCH_DEVICE_SPECIFIED_SUCCESS,
  FETCH_DEVICE_SPECIFIED_ERROR,
  GET_PROPERTY_DATA_REQUEST,
  GET_PROPERTY_DATA_SUCCESS,
  CREATE_DEVICE_DATA_REQUEST,
  CREATE_DEVICE_DATA_SUCCESS,
  CREATE_DEVICE_DATA_ERROR,
  UPDATE_DEVICE_DATA_REQUEST,
  UPDATE_DEVICE_DATA_ERROR,
  UPDATE_DEFAULT_DATA_REQUEST,
  UPDATE_DEFAULT_DATA_ERROR,
  UPDATE_DEFAULT_THRESHOLD_REQUEST,
  UPDATE_DEFAULT_THRESHOLD_ERROR,
  DELETE_DEVICE_DATA_REQUEST,
  DELETE_DEVICE_DATA_ERROR,
  SYNC_FRIENDLY_NAME_REQUEST,
  SYNC_FRIENDLY_NAME_ERROR,
  FETCH_FRIENDLY_NAME_REQUEST,
  FETCH_FRIENDLY_NAME_SUCCESS,
  FETCH_FRIENDLY_NAME_ERROR,
  DELETE_ERROR_LOG_DATA_REQUEST,
  DELETE_ERROR_LOG_DATA_ERROR,
  DOWNLOAD_DEVICE_FIRMWARE_REQUEST,
  DOWNLOAD_DEVICE_FIRMWARE_SUCCESS,
  DOWNLOAD_DEVICE_FIRMWARE_ERROR,
  FETCH_ALERT_LOG_DATA_REQUEST,
  FETCH_ALERT_LOG_DATA_SUCCESS,
  FETCH_ALERT_LOG_DATA_ERROR,
  DELETE_ALERT_LOG_DATA_REQUEST,
  DELETE_ALERT_LOG_DATA_ERROR,
  FETCH_DIAGNOSTIC_PANEL_DATA_REQUEST,
  FETCH_DIAGNOSTIC_PANEL_DATA_SUCCESS,
  FETCH_DIAGNOSTIC_PANEL_DATA_ERROR,
  SAVE_DIAGNOSTIC_PANEL_DATA_REQUEST,
  SAVE_DIAGNOSTIC_PANEL_DATA_SUCCESS,
  SAVE_DIAGNOSTIC_PANEL_DATA_ERROR,
  FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_REQUEST,
  FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_SUCCESS,
  FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_ERROR,
  FETCH_DIAGNOSTIC_PANEL_REQUEST,
  FETCH_DIAGNOSTIC_PANEL_SUCCESS,
  FETCH_DIAGNOSTIC_PANEL_ERROR,
  SAVE_USER_ORDER_REQUEST,
  SAVE_USER_ORDER_ERROR,
  SAVE_USER_ORDER_SUCCESS,
  FETCH_ERROR_LOG_CSV_SUCCESS,
  FETCH_ERROR_LOG_CSV_ERROR,
  FETCH_ALERT_LOG_CSV_REQUEST,
  FETCH_ALERT_LOG_CSV_SUCCESS,
  FETCH_ALERT_LOG_CSV_ERROR
} from "./actions";

import {
  SUCCESS_NOTIFICATION_REQUEST,
  ERROR_NOTIFICATION_REQUEST,
} from "../notifications/actions";

export const getDeviceList = (action$) => {
  return action$.pipe(
    ofType(FETCH_DEVICE_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.getDeviceList(payload).pipe(
        map((response) => {
          return dispatch(FETCH_DEVICE_SUCCESS, JSON.parse(response));
        }),
        catchError((error) => {
          return dispatch(FETCH_DEVICE_ERROR, error);
        })
      );
    })
  );
};

export const getDevicePropertyList = (action$) => {
  return action$.pipe(
    ofType(FETCH_DEVICE_PROPERTY_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.getDevicePropertyList(payload).pipe(
        map((response) => {
          return dispatch(FETCH_DEVICE_PROPERTY_SUCCESS, JSON.parse(response));
        }),
        catchError((error) => {
          return [
            dispatch(FETCH_DEVICE_PROPERTY_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: `Please select another device, property are not linked with this device.`,
              },
            }),
          ];
        })
      );
    })
  );
};

export const getDevicePropertySetList = (action$) => {
  return action$.pipe(
    ofType(FETCH_DEVICE_PROPERTY_SET_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.getDevicePropertySetList(payload).pipe(
        map((response) => {
          return dispatch(
            FETCH_DEVICE_PROPERTY_SET_SUCCESS,
            JSON.parse(response)
          );
        }),
        catchError((error) => {
          return [dispatch(FETCH_DEVICE_PROPERTY_SET_ERROR, error)];
        })
      );
    })
  );
};

export const getDeviceDetailById = (action$) => {
  return action$.pipe(
    ofType(GET_DEVICE_DETAIL_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.getDeviceDetailById(payload).pipe(
        mergeMap((deviceResponse) => {
          return deviceService.getDevicePropertyDetails(payload).pipe(
            mergeMap((response) => {
              let obj = {
                device: JSON.parse(deviceResponse),
                property: JSON.parse(response),
              };
              return [dispatch(GET_DEVICE_DETAIL_SUCCESS, obj)];
            }),
            catchError(() => {
              let obj = {
                device: JSON.parse(deviceResponse),
              };
              return [dispatch(GET_DEVICE_DETAIL_PROPERTY_ERROR, obj)];
            })
          );
        }),
        catchError((error) => {
          return [dispatch(GET_DEVICE_DETAIL_ERROR, error)];
        })
      );
    })
  );
};

export const addDevice = (action$) => {
  return action$.pipe(
    ofType(ADD_DEVICE_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.addDevice(payload).pipe(
        mergeMap((response) => {
          return [
            dispatch(ADD_DEVICE_SUCCESS, JSON.parse(response.response)),
            dispatch(SUCCESS_NOTIFICATION_REQUEST, {
              message: "Device added successfully",
            }),
          ];
        }),
        catchError((error) => {
          return [
            dispatch(ADD_DEVICE_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: error.response,
              },
            }),
          ];
        })
      );
    })
  );
};

export const deleteDevice = (action$) => {
  return action$.pipe(
    ofType(DELETE_DEVICE_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.deleteDevice(payload).pipe(
        map(() => {
          delete payload.deviceId;
          return dispatch(FETCH_DEVICE_REQUEST, payload);
        }),
        catchError((error) => [
          dispatch(DELETE_DEVICE_ERROR, error),
          dispatch(ERROR_NOTIFICATION_REQUEST, {
            response: {
              message: error.response,
            },
          }),
        ])
      )
    )
  );
};

export const updateDevice = (action$) => {
  return action$.pipe(
    ofType(UPDATE_DEVICE_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.updateDevice(payload).pipe(
        mergeMap(() => {
          return [dispatch(UPDATE_DEVICE_SUCCESS)];
        }),
        catchError((error) => {
          return [
            dispatch(UPDATE_DEVICE_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: error.response,
              },
            }),
          ];
        })
      );
    })
  );
};

export const createPropertySet = (action$) => {
  return action$.pipe(
    ofType(CREATE_PROPERTY_SET_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.addPropertySet(payload).pipe(
        mergeMap(() => {
          return [dispatch(CREATE_PROPERTY_SET_SUCCESS)];
        }),
        catchError((error) => {
          return [
            dispatch(CREATE_PROPERTY_SET_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: error.response,
              },
            }),
          ];
        })
      );
    })
  );
};

export const deletePropertySet = (action$) => {
  return action$.pipe(
    ofType(DELETE_PROPERTY_SET_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.deletePropertySet(payload).pipe(
        map(() => {
          delete payload.devicePropertySetId;
          return dispatch(GET_DEVICE_DETAIL_REQUEST, payload.deviceId);
        }),
        catchError((error) => [
          dispatch(DELETE_PROPERTY_SET_ERROR, error),
          dispatch(ERROR_NOTIFICATION_REQUEST, {
            response: {
              message: error.response,
            },
          }),
        ])
      )
    )
  );
};

export const resetDeviceData = (action$) =>
  action$.pipe(
    ofType(RESET_REQUEST),
    mergeMap(() => {
      return [dispatch(RESET_SUCCESS)];
    })
  );

export const getPropertySetDetailsById = (action$) => {
  return action$.pipe(
    ofType(FETCH_PROPERTY_SET_DETAIL_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.getPropertySetDetailsById(payload).pipe(
        mergeMap((deviceResponse) => {
          return [
            dispatch(
              FETCH_PROPERTY_SET_DETAIL_SUCCESS,
              JSON.parse(deviceResponse)
            ),
          ];
        }),
        catchError((error) => {
          return [
            dispatch(FETCH_PROPERTY_SET_DETAIL_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: error.response,
              },
            }),
          ];
        })
      )
    )
  );
};

export const updatePropertySet = (action$) => {
  return action$.pipe(
    ofType(UPDATE_PROPERTY_SET_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.updatePropertySet(payload).pipe(
        mergeMap(() => {
          return [dispatch(UPDATE_PROPERTY_SET_SUCCESS)];
        }),
        catchError((error) => {
          return [
            dispatch(UPDATE_PROPERTY_SET_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: error.response,
              },
            }),
          ];
        })
      );
    })
  );
};

export const getPropertyDetailsById = (action$) => {
  return action$.pipe(
    ofType(GET_DEVICE_PROPERTY_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.getPropertyDetailsById(payload).pipe(
        mergeMap((deviceResponse) => {
          return [
            dispatch(GET_DEVICE_PROPERTY_SUCCESS, JSON.parse(deviceResponse)),
          ];
        }),
        catchError((error) => {
          return [
            dispatch(GET_DEVICE_PROPERTY_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: error.response,
              },
            }),
          ];
        })
      )
    )
  );
};

export const updateProperty = (action$) => {
  return action$.pipe(
    ofType(UPDATE_DEVICE_PROPERTY_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.updateProperty(payload).pipe(
        mergeMap(() => {
          return [dispatch(UPDATE_DEVICE_PROPERTY_SUCCESS)];
        }),
        catchError((error) => {
          return [
            dispatch(UPDATE_DEVICE_PROPERTY_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: error.response,
              },
            }),
          ];
        })
      );
    })
  );
};

export const createProperty = (action$) => {
  return action$.pipe(
    ofType(CREATE_DEVICE_PROPERTY_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.createProperty(payload).pipe(
        mergeMap(() => {
          return [dispatch(CREATE_DEVICE_PROPERTY_SUCCESS)];
        }),
        catchError((error) => {
          return [
            dispatch(CREATE_DEVICE_PROPERTY_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: error.response,
              },
            }),
          ];
        })
      );
    })
  );
};

export const deleteProperty = (action$) => {
  return action$.pipe(
    ofType(DELETE_PROPERTY_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.deleteProperty(payload).pipe(
        map(() => {
          delete payload.devicePropertySetId;
          return dispatch(GET_DEVICE_DETAIL_REQUEST, payload.deviceId);
        }),
        catchError((error) => [
          dispatch(DELETE_PROPERTY_ERROR, error),
          dispatch(ERROR_NOTIFICATION_REQUEST, {
            response: {
              message: error.response,
            },
          }),
        ])
      )
    )
  );
};

export const getDeviceFirmwareList = (action$) => {
  return action$.pipe(
    ofType(FETCH_DEVICE_FIRMWARE_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.getDeviceFirmwareList(payload).pipe(
        mergeMap((response) => {
          return [
            dispatch(FETCH_DEVICE_FIRMWARE_SUCCESS, JSON.parse(response)),
          ];
        }),
        catchError((error) => [
          dispatch(FETCH_DEVICE_FIRMWARE_ERROR, error),
          dispatch(ERROR_NOTIFICATION_REQUEST, {
            response: {
              message: error.response,
            },
          }),
        ])
      )
    )
  );
};

export const getFirmwareDetailsById = (action$) => {
  return action$.pipe(
    ofType(FETCH_FIRMWARE_DETAIL_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.getFirmwareDetailsById(payload).pipe(
        mergeMap((response) => {
          return [
            dispatch(FETCH_FIRMWARE_DETAIL_SUCCESS, JSON.parse(response)),
          ];
        }),
        catchError((error) => [
          dispatch(FETCH_FIRMWARE_DETAIL_ERROR, error),
          dispatch(ERROR_NOTIFICATION_REQUEST, {
            response: {
              message: error.response,
            },
          }),
        ])
      )
    )
  );
};

export const updateFirmwareDetailsById = (action$) => {
  return action$.pipe(
    ofType(UPDATE_FIRMWARE_DETAIL_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.updateFirmwareDetailsById(payload).pipe(
        mergeMap(() => [dispatch(UPDATE_FIRMWARE_DETAIL_SUCCESS)]),
        catchError((error) => [
          dispatch(UPDATE_FIRMWARE_DETAIL_ERROR, error),
          dispatch(ERROR_NOTIFICATION_REQUEST, {
            response: {
              message: error.response,
            },
          }),
        ])
      )
    )
  );
};

export const createFirmwareDetailsById = (action$) => {
  return action$.pipe(
    ofType(CREATE_FIRMWARE_DETAIL_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.createFirmwareDetailsById(payload).pipe(
        mergeMap(() => [dispatch(CREATE_FIRMWARE_DETAIL_SUCCESS)]),
        catchError((error) => [
          dispatch(CREATE_FIRMWARE_DETAIL_ERROR, error),
          dispatch(ERROR_NOTIFICATION_REQUEST, {
            response: {
              message: error.response,
            },
          }),
        ])
      )
    )
  );
};

export const deleteFirmwareDetailsById = (action$) => {
  return action$.pipe(
    ofType(DELETE_DEVICE_FIRMWARE_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.deleteFirmwareDetailsById(payload).pipe(
        mergeMap(() => {
          delete payload.firmwareId;
          return [dispatch(FETCH_DEVICE_FIRMWARE_REQUEST, payload)];
        }),
        catchError((error) => [
          dispatch(DELETE_DEVICE_FIRMWARE_ERROR, error),
          dispatch(ERROR_NOTIFICATION_REQUEST, {
            response: {
              message: error.response,
            },
          }),
        ])
      )
    )
  );
};

export const saveUserDeviceOrder = (action$) => {
  return action$.pipe(
    ofType(SAVE_USER_ORDER_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.createDeviceData(payload).pipe(
        mergeMap(() => {
          return [dispatch(SAVE_USER_ORDER_SUCCESS)];
        }),
        catchError((err) => {
          return [dispatch(SAVE_USER_ORDER_ERROR, err)];
        })
      );
    })
  );
};

export const duplicateDevice = (action$) => {
  return action$.pipe(
    ofType(DUPLICATE_DEVICE_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.getDeviceDetailById(payload.deviceId).pipe(
        mergeMap((deviceData) => {
          return deviceService.getDevicePropertyDetails(payload.deviceId).pipe(
            mergeMap((propertyData) => {
              return groupAllDeviceData(
                JSON.parse(deviceData),
                JSON.parse(propertyData).content,
                payload
              );
            }),
            catchError(() => {
              return groupAllDeviceData(JSON.parse(deviceData), [], payload);
            })
          );
        }),
        catchError((error) => {
          return [
            dispatch(DUPLICATE_DEVICE_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: error.response,
              },
            }),
          ];
        })
      )
    )
  );
};

let groupAllDeviceData = (deviceData, propertyData, payload) => {
  const duplicateDeviceObj = {
    description: deviceData.description,
    maxDataAgeDays: deviceData.maxDataAgeDays,
    name: payload.newDeviceName,
    sequentialFirmwareUpgrade: deviceData.sequentialFirmwareUpgrade,
    status: deviceData.status,
    tenantId: deviceData.tenantId,
  };
  return deviceService.addDevice(duplicateDeviceObj).pipe(
    mergeMap((response) => {
      if (propertyData.length === 0) {
        return [
          dispatch(DUPLICATE_DEVICE_SUCCESS),
          dispatch(FETCH_DEVICE_REQUEST, payload.currentPage),
        ];
      } else {
        const newDeviceDetails = JSON.parse(response.response);
        const newDevicePropertySets = propertyData.reduce((acc, val) => {
          return [
            ...acc,
            deviceService.addPropertySet({
              propertyInfo: {
                collectionMethod: val?.collectionMethod,
                description: val?.description,
                deviceId: newDeviceDetails.deviceId,
                deviceProperties: [],
                name: val?.name,
                rolesAllowedToSpecify: val?.rolesAllowedToSpecify,
              },
            }),
          ];
        }, []);
        return forkJoin(newDevicePropertySets).pipe(
          mergeMap((result) => {
            const newPropertyData = propertyData.reduce((acc, val, index) => {
              const propertyData = val.deviceProperties.map((data) => {
                data.devicePropertySet = [
                  {
                    devicePropertySetId: JSON.parse(result[index].response)
                      .devicePropertySetId,
                  },
                ];
                data.deviceDataModelId = newDeviceDetails.deviceId;
                delete data.devicePropertyId;
                return deviceService.createProperty(data);
              });
              return [...acc, ...propertyData];
            }, []);
            return forkJoin(newPropertyData).pipe(
              mergeMap(() => {
                return [
                  dispatch(DUPLICATE_DEVICE_SUCCESS),
                  dispatch(FETCH_DEVICE_REQUEST, payload.currentPage),
                ];
              }),
              catchError((error) => {
                return [
                  dispatch(DUPLICATE_DEVICE_ERROR, error),
                  dispatch(ERROR_NOTIFICATION_REQUEST, {
                    response: {
                      message:
                        "Device created successfully, but error occured while linking property.",
                    },
                  }),
                ];
              })
            );
          }),
          catchError((error) => {
            return [
              dispatch(DUPLICATE_DEVICE_ERROR, error),
              dispatch(ERROR_NOTIFICATION_REQUEST, {
                response: {
                  message:
                    "Device created successfully, but error occured while linking property sets.",
                },
              }),
            ];
          })
        );
      }
    }),
    catchError((error) => {
      return [
        dispatch(DUPLICATE_DEVICE_ERROR, error),
        dispatch(ERROR_NOTIFICATION_REQUEST, {
          response: {
            message: error.response,
          },
        }),
      ];
    })
  );
};

export const resetGraphData = (action$) => {
  return action$.pipe(
    ofType(RESET_GRAPH_REQUEST),
    mergeMap(() => [dispatch(RESET_GRAPH_SUCCESS)])
  );
};

export const getDeviceSpecifiedData = (action$) => {
  return action$.pipe(
    ofType(FETCH_DEVICE_SPECIFIED_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.getDeviceList({ nameLike: payload.deviceName }).pipe(
        mergeMap((list) => {
          const linkedDevice = JSON.parse(list).content;
          const propertyDeviceReq = linkedDevice.map((val) =>
            deviceService.getDevicePropertySetList(val.deviceId)
          );
          return forkJoin(propertyDeviceReq).pipe(
            mergeMap((response) => {
              const devData = response.reduce((acc, value) => {
                const val = JSON.parse(value).content[0];
                let dataTypes = [];
                const devicePropertyCodes = val.deviceProperties
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .reduce((acc, data) => {
                    dataTypes = [...dataTypes, data.dataType];
                    return [...acc, data.propertyCode];
                  }, []);
                let obj = {};
                obj[val.name] = {
                  deviceDataModelId: val.deviceDataModelId,
                  devicePropertyCodes,
                  dataTypes,
                  devicePropertySetId: val.devicePropertySetId,
                };
                if (payload.macAddress && payload.macAddress !== "all") {
                  let defaultObj = { key: "", operator: "Equal", value: payload.macAddress, valueFrom: "", valueTo: "", isDateValue: false, dateTimeRangeValue: null}

                  const DEVICE_TYPE_KEY_MAP = {
                    "pVITAL": "DevidPvital",
                    "IDENTITY": "Devid",
                    "sVITAL": "DevidSvital",
                    "PARAMS": "DevidParams",
                    "THRESHOLDS": "DevidThresholds",
                    "DevHealth": "DevidDevhealth",
                    "ALERTS": "DevidAlerts",
                    "pStats": "DevidPs"
                  };
                  defaultObj.key = DEVICE_TYPE_KEY_MAP[val.name];
                  obj[val.name].deviceCriteria = [defaultObj];
                }
                return { ...acc, ...obj };
              }, {});
              return Object.keys(devData).reduce(
                (acc, val) => {
                  if (val !== "pVITAL" && val !== "sVITAL") {
                    return [
                      ...acc,
                      dispatch(GET_PROPERTY_DATA_REQUEST, {
                        payload: {
                          pageNumber: 0,
                          pageSize: 100,
                          sortBy: "maxValueProvidedOn",
                          sortOrder: "DESC",
                        },
                        data: {
                          ...devData[val],
                          ownerFilter: { users: payload.users },
                        },
                        type: val,
                      }),
                    ];
                  }
                  return acc;
                },
                [dispatch(FETCH_DEVICE_SPECIFIED_SUCCESS, devData)]
              );
            }),
            catchError((error) => {
              return [dispatch(FETCH_DEVICE_SPECIFIED_ERROR, error)];
            })
          );
        }),
        catchError((error) => {
          return [dispatch(FETCH_DEVICE_SPECIFIED_ERROR, error)];
        })
      );
    })
  );
};

export const getPropertyData = (action$) => {
  return action$.pipe(
    ofType(GET_PROPERTY_DATA_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.fetchTableDeviceData(payload).pipe(
        mergeMap((response) => {
          let data = JSON.parse(response.response).content;
          let obj = {};
          obj[payload.type] = {
            data,
            totalElements: JSON.parse(response.response).totalElements,
            devicePropertySetId: payload.data.devicePropertySetId,
          };
          return [dispatch(GET_PROPERTY_DATA_SUCCESS, obj)];
        }),
        catchError(() => {
          let obj = {};
          obj[payload.type] = {
            data: [],
            totalElements: 0,
          };
          return [dispatch(GET_PROPERTY_DATA_SUCCESS, obj)];
        })
      );
    })
  );
};

export const createDeviceData = (action$) => {
  return action$.pipe(
    ofType(CREATE_DEVICE_DATA_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.createDeviceData(payload).pipe(
        mergeMap(() => {
          return [dispatch(CREATE_DEVICE_DATA_SUCCESS)];
        }),
        catchError((err) => {
          return [dispatch(CREATE_DEVICE_DATA_ERROR, err)];
        })
      );
    })
  );
};

export const updateDeviceData = (action$) => {
  return action$.pipe(
    ofType(UPDATE_DEVICE_DATA_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.createDeviceData(payload.reqObj).pipe(
        mergeMap(() => {
          return [dispatch(GET_PROPERTY_DATA_REQUEST, payload.paginationObj)];
        }),
        catchError((err) => {
          return [dispatch(UPDATE_DEVICE_DATA_ERROR, err)];
        })
      );
    })
  );
};

export const updateDefaultData = (action$) => {
  return action$.pipe(
    ofType(UPDATE_DEFAULT_DATA_REQUEST),
    mergeMap(({ payload }) => {
      return usersService.getUserDetailsById(payload.user.ownerId).pipe(
        mergeMap((response) => {
          const userCompany = JSON.parse(response).roles[0].supplier;
          const companyInfo = payload.companyList.find(
            (val) => val.supplierId === userCompany.supplierId
          );
          let data = {
            DevidThresholds:
              payload.paginationObj.type === "PARAMS"
                ? payload.user.data.DevidParams.value
                : payload.user.data.DevidThresholds.value,
          };
          const macType =
            payload.paginationObj.type === "PARAMS"
              ? "DevidParams"
              : "DevidThresholds";
          const codes = payload.codes.filter((val) => val !== macType);
          data = codes.reduce((acc, val) => {
            const field = companyInfo.customData.find(
              (cVal) => cVal.field.name === val
            );
            let obj = {};
            obj[val] = field?.fieldData;
            return { ...acc, ...obj };
          }, data);
          if (payload.paginationObj.type === "PARAMS") {
            data.ParamsLastupdate = moment()
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss");
          } else {
            data.ThresholdLastupdate = moment()
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss");
          }
          let reqObj = {
            devicePropertySetId: payload.devicePropertySetId,
            deviceDataModelId: payload.deviceDataModelId,
            deviceDataId: payload.deviceDataId,
            data,
          };
          return [
            dispatch(UPDATE_DEVICE_DATA_REQUEST, {
              paginationObj: payload.paginationObj,
              reqObj: reqObj,
            }),
          ];
        }),
        catchError((err) => {
          return [dispatch(UPDATE_DEFAULT_DATA_ERROR, err)];
        })
      );
    })
  );
};

export const updateDefaultValueInCompany = (action$) => {
  return action$.pipe(
    ofType(UPDATE_DEFAULT_THRESHOLD_REQUEST),
    mergeMap(({ payload }) => {
      return usersService
        .getUserList({
          role: "Patient",
          supplierId: payload.supplierId,
          sortBy: "firstName",
          sortOrder: "ASC",
        })
        .pipe(
          mergeMap((response) => {
            return patientDeviceService
              .getPatientDeviceList({
                sortBy: "createdOn",
                sortOrder: "DESC",
              })
              .pipe(
                mergeMap((presponse) => {
                  let thresholdObj = {
                    payload: {
                      sortBy: "maxValueProvidedOn",
                      sortOrder: "DESC",
                    },
                    data: {
                      deviceId: payload.paginationObj.data.deviceId,
                      devicePropertyCodes:
                        payload.paginationObj.data.devicePropertyCodes,
                    },
                  };
                  return deviceService.fetchTableDeviceData(thresholdObj).pipe(
                    mergeMap((thresholdResponse) => {
                      const thresholdData = JSON.parse(
                        thresholdResponse.response
                      ).content;
                      const userList = JSON.parse(response).content;
                      const patientDevice = JSON.parse(presponse).content;
                      const macType =
                        payload.paginationObj.type === "PARAMS"
                          ? "DevidParams"
                          : "DevidThresholds";
                      const codes = payload.paginationObj.data.devicePropertyCodes.filter(
                        (val) => val !== macType
                      );
                      const defaultValues = codes.reduce((acc, val) => {
                        let defaultValue = payload.customData.find(
                          (v) => v.field.name === val
                        );
                        let obj = {};
                        obj[val] = defaultValue?.fieldData;
                        return { ...acc, ...obj };
                      }, {});

                      const updatedUserList = userList.reduce((acc, val) => {
                        let macId = patientDevice.find(
                          (pVal) => pVal.user.userId === val.userId
                        );
                        if (macId?.value) {
                          const currentThreshold = thresholdData.find(
                            (data) => data.user?.userId === val.userId
                          );
                          let newObj = {};
                          newObj[macType] = macId.value;
                          return [
                            ...acc,
                            deviceService.createDeviceData({
                              devicePropertySetId:
                                payload.paginationObj.data.devicePropertySetId,
                              deviceId: payload.paginationObj.data.deviceId,
                              deviceDataId: currentThreshold?.deviceDataId,
                              data: { ...defaultValues, ...newObj },
                            }),
                          ];
                        } else {
                          return acc;
                        }
                      }, []);
                      return forkJoin(updatedUserList).pipe(
                        mergeMap((updateResponse) => {
                          return [
                            dispatch(
                              GET_PROPERTY_DATA_REQUEST,
                              payload.paginationObj
                            ),
                          ];
                        }),
                        catchError((err) => {
                          return [
                            dispatch(UPDATE_DEFAULT_THRESHOLD_ERROR, err),
                          ];
                        })
                      );
                    }),
                    catchError((err) => {
                      return [dispatch(UPDATE_DEFAULT_THRESHOLD_ERROR, err)];
                    })
                  );
                }),
                catchError((err) => {
                  return [dispatch(UPDATE_DEFAULT_THRESHOLD_ERROR, err)];
                })
              );
          }),
          catchError((err) => {
            return [dispatch(UPDATE_DEFAULT_THRESHOLD_ERROR, err)];
          })
        );
    })
  );
};

export const deleteDeviceData = (action$) => {
  return action$.pipe(
    ofType(DELETE_DEVICE_DATA_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.deleteDeviceData(payload).pipe(
        mergeMap(() => {
          return [dispatch(GET_PROPERTY_DATA_REQUEST, payload.paginationObj)];
        }),
        catchError((err) => {
          return [dispatch(DELETE_DEVICE_DATA_ERROR, err)];
        })
      );
    })
  );
};

export const syncFriendlyName = (action$) => {
  return action$.pipe(
    ofType(SYNC_FRIENDLY_NAME_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService.fetchUserDeviceData(payload.userObj).pipe(
        mergeMap((res) => {
          const devId = payload.reqObj.data.Devid;
          const searchedData = JSON.parse(res.response).content.find((val) => {
            const currentId =
              payload.type === "PARAMS"
                ? val.data.DevidParams.value
                : val.data.DevidThresholds.value;
            if (currentId === devId) {
              return val;
            }
          });
          let data = Object.keys(searchedData.data).reduce((acc, val) => {
            let obj = {};
            obj[val] = searchedData.data[val].value;
            return { ...acc, ...obj };
          }, {});
          if (payload.type === "THRESHOLDS") {
            data = {
              ...data,
              ...{ FriendlyNameT: payload.reqObj.data.FriendlyName },
            };
          } else if (payload.type === "PARAMS") {
            data = {
              ...data,
              ...{ FriendlyNamePa: payload.reqObj.data.FriendlyName },
            };
          }
          let reqObj = {
            devicePropertySetId:
              payload.device?.deviceTable[payload.type].devicePropertySetId,
            deviceId: payload.userInfo?.deviceId,
            deviceDataId: searchedData.deviceDataId,
            data,
          };
          let paginationObj = {
            payload: {
              pageNumber: payload.paginationData[payload.type]?.page,
              pageSize: payload.paginationData[payload.type]?.rowsPerPage,
              sortBy: "maxValueProvidedOn",
              sortOrder: "DESC",
            },
            data: {
              deviceId: payload.userInfo?.deviceId,
              devicePropertyCodes:
                payload.device?.deviceTableHeader[payload.type]
                  .devicePropertyCodes,
              devicePropertySetId:
                payload.device?.deviceTable[payload.type].devicePropertySetId,
            },
            ownerFilter: {
              users: [payload.userInfo.userId],
            },
            type: payload.type,
          };
          return [
            dispatch(UPDATE_DEVICE_DATA_REQUEST, { reqObj, paginationObj }),
          ];
        }),
        catchError((err) => {
          return [dispatch(SYNC_FRIENDLY_NAME_ERROR, err)];
        })
      );
    })
  );
};

let arrayToCsv = (data) => {
  return data
    .map((row) =>
      row
        .map(String)
        .map((v) => v.replaceAll('"', '""'))
        .map((v) => `"${v}"`)
        .join(",")
    )
    .join("\r\n");
};

let downloadBlob = (content, filename, contentType, fileSize) => {
  // Create a blob
  var blob = new Blob([content], { type: contentType });
  var url = URL.createObjectURL(blob);

  // Create a link to download it
  var pom = document.createElement("a");
  pom.href = url;
  pom.setAttribute("download", filename);
  pom.click();
};

export const fetchFriendlyNameData = (action$) => {
  return action$.pipe(
    ofType(FETCH_FRIENDLY_NAME_REQUEST),
    mergeMap(({ payload }) => {
      let data = {
        payload: { sortBy: "maxValueProvidedOn", sortOrder: "DESC" },
        data: { deviceDataModelId: `${payload.deviceDataModelId}-id`, devicePropertyCodes: ["Devid", "FriendlyName", "Timezone"] }
      };
      if (payload?.userId) {
        data.data.ownerFilter = { users: [payload.userId] }
      }

      return deviceService.fetchTableDeviceData(data).pipe(
        mergeMap((identityData) => {
          let identityResponse = JSON.parse(identityData.response).content;
          let friendlyNameData = identityResponse.reduce((acc, val) => {
            return [
              ...acc, {
                macId: val.data.Devid.value,
                friendlyName: val.data.FriendlyName.value,
                timeZone: val.data?.Timezone?.value || DEFAULT_TIME,
              },
            ];
          }, []);
          let finalObj = {
            user: payload.user || "",
            friendlyNameData,
          };

          return [dispatch(FETCH_FRIENDLY_NAME_SUCCESS, finalObj)];
        }),
        catchError(() => {
          return [
            dispatch(FETCH_FRIENDLY_NAME_ERROR),
            dispatch(ERROR_NOTIFICATION_REQUEST, { response: { message: "Friendly Name not found." } }),
          ];
        })
      );
    })
  );
};

export const deleteErrorLogData = (action$) => {
  return action$.pipe(
    ofType(DELETE_ERROR_LOG_DATA_REQUEST),
    mergeMap(({ payload }) => {
      const filter = payload.filter;
      return deviceService.deleteMultipleDeviceData(payload).pipe(
        mergeMap(() => {
          return [
            dispatch(SUCCESS_NOTIFICATION_REQUEST, {
              message: `Error logs deleted successfully`,
            }),
          ];
        }),
        catchError((error) => {
          return [
            dispatch(DELETE_ERROR_LOG_DATA_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: `Error while deleting error logs`,
              },
            }),
          ];
        })
      );
    })
  );
};

export const downloadFirmwareVersion = (action$) => {
  return action$.pipe(
    ofType(DOWNLOAD_DEVICE_FIRMWARE_REQUEST),
    mergeMap(({ payload }) =>
      deviceService.downloadFirmwareVersion(payload.firmwareId).pipe(
        map((response) => {
          const responseType = response.xhr.getResponseHeader('content-type');
          const responseLength = response.xhr.getResponseHeader('content-type');
          downloadBlob(response.response, payload.fileName, responseType, responseLength);
          return dispatch(DOWNLOAD_DEVICE_FIRMWARE_SUCCESS, payload.deviceId);
        }),
        catchError((error) => [
          dispatch(DOWNLOAD_DEVICE_FIRMWARE_ERROR, error),
          dispatch(ERROR_NOTIFICATION_REQUEST, {
            response: {
              message: error.response,
            },
          }),
        ])
      )
    )
  );
};

export const getAlertLogData = (action$) => {
  return action$.pipe(
    ofType(FETCH_ALERT_LOG_DATA_REQUEST),
    mergeMap(({ payload }) => {
      const newIdentityDataFrame = payload?.identityDataFrame && [...payload.identityDataFrame] || [];
      delete payload.identityDataFrame;
      return deviceService.fetchTableDeviceData(payload).pipe(
        mergeMap((response) => {
          let identityCalls = []
          if (newIdentityDataFrame?.length > 0) {
            identityCalls = newIdentityDataFrame?.reduce((acc, val) => {
              return [
                ...acc,
                deviceService
                  .fetchUserFirstPageData(val)
                  .pipe(catchError((error) => of(error))),
              ];
            }, []);
            return forkJoin(identityCalls).pipe(
              mergeMap((result) => {
                let macIdObj = [];
                let deviceData = JSON.parse(response.response);
                deviceData.macIdObj = [];
                if (result.length > 0) {
                  result.forEach(val => {
                    const parsedData = JSON.parse(val.response);
                    if (
                      parsedData.content.length > 0 &&
                      parsedData.content[0].data &&
                      parsedData.content[0].data.Timezone &&
                      parsedData.content[0].data.Timezone.value &&
                      parsedData.content[0].data.Devid &&
                      parsedData.content[0].data.Devid.value
                    ) {
                      macIdObj[parsedData.content[0].data.Devid.value] = parsedData.content[0].data.Timezone.value;
                    }
                  });
                  deviceData.macIdObj = macIdObj
                  return [dispatch(FETCH_ALERT_LOG_DATA_SUCCESS, deviceData)];
                } else {
                  return [dispatch(FETCH_ALERT_LOG_DATA_SUCCESS, deviceData)];
                }
              }),
              catchError((error) => {
                return [
                  dispatch(FETCH_ALERT_LOG_DATA_ERROR, error),
                  dispatch(ERROR_NOTIFICATION_REQUEST, {
                    response: {
                      message: `Error while fetching logs`,
                    },
                  }),
                ];
              })
            );
          } else {
            let deviceData = JSON.parse(response.response);
            deviceData.macIdObj = [];
            return [dispatch(FETCH_ALERT_LOG_DATA_SUCCESS, deviceData)];
          }
        }),
        catchError((error) => {
          return [
            dispatch(FETCH_ALERT_LOG_DATA_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: `Error while fetching logs`,
              },
            }),
          ];
        })
      );
    })
  );
};

export const deleteAlertLogData = (action$) => {
  return action$.pipe(
    ofType(DELETE_ALERT_LOG_DATA_REQUEST),
    mergeMap(({ payload }) => {
      const filter = payload.filter;
      return deviceService.deleteMultipleAlertDeviceData(payload).pipe(
        mergeMap(() => {
          return [
            dispatch(FETCH_ALERT_LOG_DATA_REQUEST, filter),
            dispatch(SUCCESS_NOTIFICATION_REQUEST, {
              message: `Alert logs deleted successfully`,
            }),
          ];
        }),
        catchError((error) => {
          return [
            dispatch(DELETE_ALERT_LOG_DATA_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: `Error while deleting alert logs`,
              },
            }),
          ];
        })
      );
    })
  );
};

export const fetchDiagnosticPanelData = (action$) => {
  return action$.pipe(
    ofType(FETCH_DIAGNOSTIC_PANEL_DATA_REQUEST),
    mergeMap(({ payload }) => {
      return forkJoin([
        deviceService
          .fetchUserDeviceAggregateData(
            payload.firstUserFirstRequest,
            payload.filter1
          )
          .pipe(catchError((error) => of(error))),
        deviceService
          .fetchUserDeviceAggregateData(
            payload.secondUserFirstRequest,
            payload.filter1
          )
          .pipe(catchError((error) => of(error))),
        deviceService
          .fetchUserDeviceAggregateData(
            payload.thirdUserFirstRequest,
            payload.filter1
          )
          .pipe(catchError((error) => of(error))),
        deviceService
          .fetchUserDeviceAggregateData(
            payload.firstUserSecondRequest,
            payload.filter2
          )
          .pipe(catchError((error) => of(error))),
        deviceService
          .fetchUserDeviceAggregateData(
            payload.secondUserSecondRequest,
            payload.filter2
          )
          .pipe(catchError((error) => of(error))),
        deviceService
          .fetchUserDeviceAggregateData(
            payload.thirdUserSecondRequest,
            payload.filter2
          )
          .pipe(catchError((error) => of(error))),
      ]).pipe(
        mergeMap((data) => {
          if (data.filter((val) => val.name === "AjaxError").length === 6) {
            return [
              dispatch(FETCH_DIAGNOSTIC_PANEL_DATA_ERROR),
              dispatch(ERROR_NOTIFICATION_REQUEST, {
                response: {
                  message: "Error: No data in the plot range selected",
                  align: "tc",
                },
              }),
            ];
          } else {
            let devId1 = "DevidPvital";
            if (payload.filter1.sortBy === "data.TimestampS.value") {
              devId1 = "DevidSvital";
            }
            let devId2 = "DevidPvital";
            if (payload.filter2.sortBy === "data.TimestampS.value") {
              devId2 = "DevidSvital";
            }
            return [
              dispatch(FETCH_DIAGNOSTIC_PANEL_DATA_SUCCESS, {
                firstUserFirstData:
                  data[0].status === 200
                    ? JSON.parse(data[0].response).content
                    : [],
                secondUserFirstData:
                  data[1].status === 200
                    ? JSON.parse(data[1].response).content
                    : [],
                thirdUserFirstData:
                  data[2].status === 200
                    ? JSON.parse(data[2].response).content
                    : [],
                firstUserSecondData:
                  data[3].status === 200
                    ? JSON.parse(data[3].response).content
                    : [],
                secondUserSecondData:
                  data[4].status === 200
                    ? JSON.parse(data[4].response).content
                    : [],
                thirdUserSecondData:
                  data[5].status === 200
                    ? JSON.parse(data[5].response).content
                    : [],

                firstUserId: payload.firstUserFirstRequest.deviceCriteria.find(
                  (val) => val.key === devId1
                ),
                secondUserId:
                  Object.keys(payload.secondUserFirstRequest).length > 0
                    ? payload.secondUserFirstRequest.deviceCriteria.find(
                      (val) => val.key === devId1
                    )
                    : [],
                thirdUserId:
                  Object.keys(payload.thirdUserFirstRequest).length > 0
                    ? payload.thirdUserFirstRequest.deviceCriteria.find(
                      (val) => val.key === devId1
                    )
                    : [],

                firstUserId2: payload.firstUserSecondRequest.deviceCriteria.find(
                  (val) => val.key === devId2
                ),
                secondUserId2:
                  Object.keys(payload.secondUserSecondRequest).length > 0
                    ? payload.secondUserSecondRequest.deviceCriteria.find(
                      (val) => val.key === devId2
                    )
                    : [],
                thirdUserId2:
                  Object.keys(payload.thirdUserSecondRequest).length > 0
                    ? payload.thirdUserSecondRequest.deviceCriteria.find(
                      (val) => val.key === devId2
                    )
                    : [],

                secondUser:
                  Object.keys(payload.secondUserFirstRequest).length > 0
                    ? true
                    : false,
                thirdUser:
                  Object.keys(payload.thirdUserFirstRequest).length > 0
                    ? true
                    : false,
                currentUserTimeZone: payload.firstUserFirstRequest.userUtcTime,
              }),
            ];
          }
        }),
        catchError((error) => {

          let errorMessage = "Error: cloud not responding";
          if (error.status === 404) {
            errorMessage = "Error: No data in the plot range selected";
          }
          return [
            dispatch(FETCH_DIAGNOSTIC_PANEL_DATA_ERROR),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: errorMessage,
                align: "tc",
              },
            }),
          ];
        })
      );
    })
  );
};

export const saveDiagnosticPanelData = (action$) => {
  return action$.pipe(
    ofType(SAVE_DIAGNOSTIC_PANEL_DATA_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService
        .getDeviceList({
          nameLike: "Diagnostic",
          pageNumber: 0,
          pageSize: 20,
        })
        .pipe(
          mergeMap((data) => {
            const parsedDeviceData = JSON.parse(data);
            if (parsedDeviceData && parsedDeviceData.content.length > 0) {
              const deviceInfo = parsedDeviceData.content[0];
              return deviceService
                .getDevicePropertySetList(deviceInfo.deviceId)
                .pipe(
                  mergeMap((dataProperty) => {
                    const parsedDeviceDataProperty = JSON.parse(dataProperty);
                    let finalObj = {
                      devicePropertySetId:
                        parsedDeviceDataProperty.content[0].devicePropertySetId,
                      deviceId: parsedDeviceDataProperty.content[0].deviceId,
                      deviceDataId: payload.deviceDataId || "",
                      data: {
                        User1: payload.userId1,
                        User2: payload.userId2,
                        User3: payload.userId3,
                        Username1: payload.userName1,
                        Username2: payload.userName2,
                        Username3: payload.userName3,
                        Friendlyname1: payload.friendlyName1,
                        Friendlyname2: payload.friendlyName2,
                        Friendlyname3: payload.friendlyName3,
                        Fname1: payload.macAddress1,
                        Fname2: payload.macAddress2,
                        Fname3: payload.macAddress3,
                        Interval: payload.interval,
                        Graph1: payload.graphProperty1,
                        Graph2: payload.graphProperty2,
                        Timezone: payload.timezone,
                        Starttime: payload.intervalStartTime,
                        Createdbyuser: payload.loggedUserId,
                        Createdbyusername: payload.createdByUserName,
                        Timeformat: payload.timeformat,
                      },
                    };
                    return deviceService.createDeviceData(finalObj).pipe(
                      mergeMap(() => {
                        return [dispatch(SAVE_DIAGNOSTIC_PANEL_DATA_SUCCESS)];
                      })
                    );
                  })
                );
            } else {
              return [
                dispatch(SAVE_DIAGNOSTIC_PANEL_DATA_ERROR),
                dispatch(ERROR_NOTIFICATION_REQUEST, {
                  response: {
                    message: "Error while saving filter data.",
                    align: "tc",
                  },
                }),
              ];
            }
          }),
          catchError((error) => {
            let errorMessage = "Error: cloud not responding";
            if (error.status === 404) {
              errorMessage = "Error: No data in the plot range selected";
            }
            return [
              dispatch(SAVE_DIAGNOSTIC_PANEL_DATA_ERROR),
              dispatch(ERROR_NOTIFICATION_REQUEST, {
                response: {
                  message: errorMessage,
                  align: "tc",
                },
              }),
            ];
          })
        );
    })
  );
};

export const fetchSavedDiagnosticPanelData = (action$) => {
  return action$.pipe(
    ofType(FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService
        .getDeviceList({
          nameLike: "Diagnostic",
          pageNumber: 0,
          pageSize: 20,
        })
        .pipe(
          mergeMap((data) => {
            const parsedDeviceData = JSON.parse(data);
            if (parsedDeviceData && parsedDeviceData.content.length > 0) {
              const deviceInfo = parsedDeviceData.content[0];
              return deviceService
                .getDevicePropertySetList(deviceInfo.deviceId)
                .pipe(
                  mergeMap((dataProperty) => {
                    const parsedDeviceDataProperty = JSON.parse(dataProperty);
                    let finalObj = {
                      deviceDataModelId:
                        parsedDeviceDataProperty.content[0].deviceDataModelId,
                      deviceCriteriaGroup: {
                        isOr: false,
                        groupOwner: null,
                        groupElements: [
                          {
                            key: "Createdbyuser",
                            operator: "Equal",
                            value: payload,
                            valueFrom: null,
                            valueTo: null,
                            isDateValue: false,
                            dateTimeRangeValue: null,
                            criteriaNumber: 1,
                          },
                        ],
                      },
                    };
                    return deviceService
                      .fetchSavedDiagnosticPanelData(finalObj)
                      .pipe(
                        mergeMap((savedResponse) => {
                          const data = JSON.parse(savedResponse.response);
                          return [
                            dispatch(
                              FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_SUCCESS,
                              data.content
                            ),
                          ];
                        })
                      );
                  })
                );
            } else {
              return [dispatch(FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_ERROR),];
            }
          }),
          catchError((error) => {
            return [dispatch(FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_ERROR),];
          })
        );
    })
  );
};

export const fetchSavedDiagnosticPanel = (action$) => {
  return action$.pipe(
    ofType(FETCH_DIAGNOSTIC_PANEL_REQUEST),
    mergeMap(({ payload }) => {
      return deviceService
        .getDeviceList({
          nameLike: "Diagnostic",
          pageNumber: 0,
          pageSize: 20,
        })
        .pipe(
          mergeMap((data) => {
            const parsedDeviceData = JSON.parse(data);
            if (parsedDeviceData && parsedDeviceData.content.length > 0) {
              const deviceInfo = parsedDeviceData.content[0];
              return deviceService
                .getDevicePropertySetList(deviceInfo.deviceId)
                .pipe(
                  mergeMap((dataProperty) => {
                    const parsedDeviceDataProperty = JSON.parse(dataProperty);
                    let finalObj = {
                      deviceDataModelId:
                        parsedDeviceDataProperty.content[0].deviceDataModelId,
                      deviceCriteriaGroup: {
                        isOr: false,
                        groupOwner: null,
                        groupElements: [],
                      },
                    };
                    return deviceService
                      .fetchSavedDiagnosticPanelData(finalObj)
                      .pipe(
                        mergeMap((savedResponse) => {
                          const data = JSON.parse(savedResponse.response);
                          return [
                            dispatch(FETCH_DIAGNOSTIC_PANEL_SUCCESS, data),
                          ];
                        })
                      );
                  })
                );
            } else {
              return [
                dispatch(FETCH_DIAGNOSTIC_PANEL_ERROR),
              ];
            }
          }),
          catchError((error) => {
            return [dispatch(FETCH_DIAGNOSTIC_PANEL_ERROR),];
          })
        );
    })
  );
};

export const fetchAlertLogCsvData = (action$) => {
  return action$.pipe(
    ofType(FETCH_ALERT_LOG_CSV_REQUEST),
    mergeMap(({ payload }) => {
      const newIdentityDataFrame = payload?.identityDataFrame && [...payload.identityDataFrame] || [];
      delete payload.identityDataFrame;
      delete payload.payload.pageNumber;
      delete payload.payload.pageSize;
      return deviceService.fetchTableDeviceData(payload).pipe(
        mergeMap((response) => {
          let identityCalls = []
          if (newIdentityDataFrame?.length > 0) {
            identityCalls = newIdentityDataFrame?.reduce((acc, val) => {
              return [
                ...acc,
                deviceService
                  .fetchUserFirstPageData(val)
                  .pipe(catchError((error) => of(error))),
              ];
            }, []);
            return forkJoin(identityCalls).pipe(
              mergeMap((result) => {
                let macIdObj = [];
                let deviceData = JSON.parse(response.response);
                deviceData.macIdObj = [];
                if (result.length > 0) {
                  result.forEach(val => {
                    const parsedData = JSON.parse(val.response);
                    if (
                      parsedData.content.length > 0 &&
                      parsedData.content[0].data &&
                      parsedData.content[0].data.Timezone &&
                      parsedData.content[0].data.Timezone.value &&
                      parsedData.content[0].data.Devid &&
                      parsedData.content[0].data.Devid.value
                    ) {
                      macIdObj[parsedData.content[0].data.Devid.value] = parsedData.content[0].data.Timezone.value;
                    }
                  });
                  deviceData.macIdObj = macIdObj
                  let csvData = convertAlertData(deviceData)
                  let csvFile = `alert-logs.csv`;
                  downloadBlob(
                    arrayToCsv(csvData),
                    csvFile,
                    "text/csv;charset=utf-8;"
                  ); 
                  return [dispatch(FETCH_ERROR_LOG_CSV_SUCCESS, deviceData)];
                } else {
                  return [dispatch(FETCH_ALERT_LOG_CSV_SUCCESS, deviceData)];
                }
              }),
              catchError((error) => {
                debugger
                return [
                  dispatch(FETCH_ALERT_LOG_CSV_ERROR, error),
                  dispatch(ERROR_NOTIFICATION_REQUEST, {
                    response: {
                      message: `Error while fetching alert logs`,
                    },
                  }),
                ];
              })
            );
          } else {
            let deviceData = JSON.parse(response.response);
            deviceData.macIdObj = [];
            let csvData = convertAlertData(deviceData)
            let csvFile = `alert-logs.csv`;
            downloadBlob(
              arrayToCsv(csvData),
              csvFile,
              "text/csv;charset=utf-8;"
            );
            return [dispatch(FETCH_ERROR_LOG_CSV_SUCCESS, deviceData)];
          }
        }),
        catchError((error) => {
          return [
            dispatch(FETCH_ERROR_LOG_CSV_ERROR, error),
            dispatch(ERROR_NOTIFICATION_REQUEST, {
              response: {
                message: `Error while fetching logs`,
              },
            }),
          ];
        })
      );
    })
  );
};

const convertAlertData = (deviceData) => {
  return deviceData.content.reduce((acc, val) => {
    let timeProvided = moment.utc(val.data.TimestampA.valueProvidedOn).local().format(CSV_DATE_FORMAT);
    if (
      val.data.TimestampA &&
      val.data.TimestampA.value &&
      val.data.DevidAlerts &&
      val.data.DevidAlerts.value &&
      deviceData.macIdObj[val.data.DevidAlerts.value]
    ) {
      let userTimeZone = timezones.find(
        (time) => time.value === deviceData.macIdObj[val.data.DevidAlerts.value]
      )
      if (userTimeZone && userTimeZone.utc && userTimeZone.utc.length > 0) {
        timeProvided = momentTz
          .utc(val.data.TimestampA.valueProvidedOn)
          .tz(userTimeZone.utc[0])
          .format(CSV_DATE_FORMAT)
      }
    }
    let obj = [
      val.owner.fullName,
      val.data.FriendlyNameA.value,
      val.data.DevidAlerts.value,
      val.data.AlertType ? val.data.AlertType.value : "",
      val.data.Atext ? val.data.Atext.value : "",
      val.data.TimestampA
        ? timeProvided
        : "",
      // deviceDataId: val.deviceDataId,
    ]
    return [...acc, obj];
  }, [['Resident Name', 'Friendly Name', 'Dev Id', 'Alert Type', 'Alert Text', 'Alert Time']]);
}